<script setup lang="ts">
import MenuItem from '~/components/menu/MenuItem.vue';
import type { SocialItem } from '~/types';
import { RedirectConfig } from '~/configs';

const socialItems: SocialItem[] = [{ icon: 'linkedin', href: RedirectConfig.linkedIn }];
const isOpened = ref<boolean>(false);

const toggleMenu = () => isOpened.value = !isOpened.value;
const closeMenu = () => isOpened.value = false;

const { getTranslation } = useLanguage();
const { common } = getTranslation();
</script>

<template>
  <nav :class="['menu', { active: isOpened }]">
    <div
      class="burger"
      @click.prevent="toggleMenu"
    >
      <span /><span /><span />
    </div>

    <div class="menu-content">
      <NuxtLink
        to="/"
        class="logo"
      >
        <NuxtImg :src="CdnConfig.healisticIconSvg" />
      </NuxtLink>

      <div class="menu-container flex flex-col h-full overflow-y-auto xs:flex-row">
        <div class="relative z-10 flex items-center gap-7 order-2 mt-auto pt-7 xs:gap-4 xs:order-1 xs:mt-0 xs:pt-0 hd:gap-7">
          <MenuSocial
            :items="socialItems"
            global-classes="!h-8"
          />

          <NuxtLink
            :to="RedirectConfig.prescriberWebapp"
            target="_blank"
            class="hidden xs:block"
          >
            <BaseButton
              style-type="gray"
              size="sm"
            >
              {{ common.buttons.header }}
            </BaseButton>
          </NuxtLink>
        </div>

        <ul class="menu-list">
          <MenuItem
            v-for="{ id, label, link, icon } in NAVIGATION"
            :key="id"
            :link
            @click="closeMenu"
          >
            <span>{{ label }}</span>
            <BaseCustomIcon
              v-if="icon"
              :icon
              class="w-7 2xs:w-10 xs:hidden"
            />
          </MenuItem>
        </ul>
      </div>
    </div>

    <div
      class="overlay"
      @click="closeMenu"
    />
  </nav>
</template>

<style scoped lang="scss">
.menu {
  @apply flex flex-col justify-center h-full xs:w-full;
  @apply relative z-100;
  @apply overflow-hidden;

  .menu-content {
    @apply pt-32 pb-8 2xs:pt-36 xs:py-0;;
    @apply bg-viola-800 xs:bg-transparent;
    @apply fixed right-0 top-0 bottom-0 xs:relative xs:inset-[initial];
    @apply translate-x-full transition-all duration-200 xs:translate-x-0;
    @apply overflow-hidden;
    @apply max-w-[90%] w-full 2xs:max-w-[28rem] xs:w-full xs:max-w-none;
    @apply xs:flex-row;
  }

  .menu-list {
    @apply relative z-50;
    @apply flex flex-col items-start xs:flex-row xs:items-center xs:gap-8;
    @apply xs:mx-auto
  }

  .logo {
    @apply fixed z-[51] top-8 left-[20px] 2xs:left-[calc((100%-min(90%,88.9vw))/2)];
    @apply w-12 h-12;
    @apply cursor-pointer;
    @apply xs:hidden;
  }

  .burger {
    @apply flex flex-col justify-between xs:hidden;
    @apply cursor-pointer;
    @apply w-6 h-5;
    @apply fixed z-[51] top-9 right-[20px] 2xs:right-[calc((100%-min(90%,88.9vw))/2)];

    span {
      @apply rounded-[2px];
      @apply w-full h-px;
      @apply bg-white;
      @apply transition-all;
    }
  }

  .overlay {
    @apply fixed inset-0 -z-1 xs:hidden;
    @apply bg-black/80 opacity-0 invisible;
    @apply backdrop-blur-sm;
    @apply transition-all duration-300;
  }

  // Active state
  &.active {
    .menu-content {
      @apply translate-x-0;
    }

    .burger {
      span {
        @apply absolute top-1/2;

        &:nth-child(1) {
          @apply rotate-45;
        }

        &:nth-child(2) {
          @apply opacity-0;
        }

        &:nth-child(3) {
          @apply -rotate-45;
        }
      }
    }

    .overlay {
      @apply opacity-100 visible;
    }
  }
}

.menu-container {
  @apply mx-auto;
  @apply w-[calc(100%-40px)] 2xs:w-[min(90%,88.9vw)] xs:w-full;
}
</style>
